import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  alpha,
  IconButton,
  Button,
  Chip,
  Divider,
  useMediaQuery,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import { 
  Close,
  Delete,
  Person,
  CalendarToday,
  Report,
  Visibility,
  Warning,
  CheckCircle,
  FilterList,
  Refresh,
  Email,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/Text-WFHCafe_WObg.png';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ReportData {
  _id: string;
  postId: string;
  postTitle: string;
  postAuthorId: string;
  postAuthorUsername: string;
  reportType: string;
  customReason?: string;
  description?: string;
  reporterId: string;
  reporterUsername: string;
  reporterEmail: string;
  timestamp: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: string;
  updatedAt: string;
}

interface UserProfile {
  id: string;
  username: string;
  profilePicture?: string;
}

interface AdminReportsActionModalProps {
  open: boolean;
  onClose: () => void;
  report: ReportData | null;
  onDeletePost: (postId: string) => Promise<void>;
  onUpdateStatus: (reportId: string, status: 'pending' | 'reviewed' | 'resolved' | 'dismissed') => Promise<void>;
  isDark: boolean;
}

const AdminReportsActionModal: React.FC<AdminReportsActionModalProps> = ({
  open,
  onClose,
  report,
  onDeletePost,
  onUpdateStatus,
  isDark,
}) => {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [postAuthorProfile, setPostAuthorProfile] = useState<UserProfile | null>(null);
  const [reporterProfile, setReporterProfile] = useState<UserProfile | null>(null);
  const [loadingProfiles, setLoadingProfiles] = useState({
    author: false,
    reporter: false,
  });
  
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldGradient = `linear-gradient(135deg, ${goldColor} 0%, #D4C690 100%)`;
  
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: 'auto',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: isMobile ? '90vh' : '80vh',
    margin: 'auto',
    mx: isMobile ? 2 : 'auto',
    overflow: 'hidden',
    padding: 0,
    position: 'relative',
  };

  useEffect(() => {
    if (report) {
      fetchProfiles();
    } else {
      setPostAuthorProfile(null);
      setReporterProfile(null);
    }
  }, [report]);

  const fetchProfiles = async () => {
    if (!report) return;

    setLoadingProfiles({ author: true, reporter: true });
    
    try {
      const token = localStorage.getItem('token');
      
      // Fetch post author profile
      if (report.postAuthorId) {
        try {
          // Try the main API endpoint first
          const response = await fetch(`${API_URL}/api/user/${report.postAuthorId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.user) {
              setPostAuthorProfile({
                id: data.user._id,
                username: data.user.username,
                profilePicture: data.user.profilePicture
              });
            } else {
              // Try fetching from all users
              await fetchUserFromAll(report.postAuthorId, 'author');
            }
          } else {
            // Try alternative endpoints
            await fetchUserFromAll(report.postAuthorId, 'author');
          }
        } catch (err) {
          console.error('Error fetching author profile:', err);
          await fetchUserFromAll(report.postAuthorId, 'author');
        }
      }

      // Fetch reporter profile
      if (report.reporterId) {
        try {
          const response = await fetch(`${API_URL}/api/user/${report.reporterId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.user) {
              setReporterProfile({
                id: data.user._id,
                username: data.user.username,
                profilePicture: data.user.profilePicture
              });
            } else {
              // Try fetching from all users
              await fetchUserFromAll(report.reporterId, 'reporter');
            }
          } else {
            // Try alternative endpoints
            await fetchUserFromAll(report.reporterId, 'reporter');
          }
        } catch (err) {
          console.error('Error fetching reporter profile:', err);
          await fetchUserFromAll(report.reporterId, 'reporter');
        }
      }
    } finally {
      setLoadingProfiles({ author: false, reporter: false });
    }
  };

  const fetchUserFromAll = async (userId: string, type: 'author' | 'reporter') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/user/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.users) {
          const foundUser = data.users.find((u: any) => u._id === userId);
          if (foundUser) {
            if (type === 'author') {
              setPostAuthorProfile({
                id: foundUser._id,
                username: foundUser.username,
                profilePicture: foundUser.profilePicture
              });
            } else {
              setReporterProfile({
                id: foundUser._id,
                username: foundUser.username,
                profilePicture: foundUser.profilePicture
              });
            }
          }
        }
      }
    } catch (err) {
      console.error(`Error fetching ${type} from all users:`, err);
    }
  };

  if (!report) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: modalStyle,
        }}
        maxWidth={false}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: isDark ? '#fff' : deepGreen }}>
            No report selected
          </Typography>
          <Button 
            onClick={onClose} 
            sx={{ 
              mt: 2,
              color: goldColor,
              '&:hover': { bgcolor: alpha(goldColor, 0.1) }
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isMobile) {
        return format(date, 'MMM d, yyyy');
      }
      return format(date, 'PPP p');
    } catch {
      return dateString;
    }
  };

  const getInitials = (username: string) => {
    return username
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Use theme colors only
  const getStatusColor = () => {
    return isDark ? goldColor : deepGreen;
  };

  const handleUpdateStatus = async (status: 'pending' | 'reviewed' | 'resolved' | 'dismissed') => {
    setActionLoading(`status-${status}`);
    try {
      await onUpdateStatus(report._id, status);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeletePost = async () => {
    setActionLoading('delete-post');
    try {
      await onDeletePost(report.postId);
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewPost = () => {
    navigate(`/post/${report.postId}`);
    onClose();
  };

  const gradientButtonStyle = {
    background: isDark ? goldGradient : lokiGradient,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '50px',
    fontWeight: 800,
    textTransform: 'none',
    fontSize: isMobile ? '0.85rem' : '1rem',
    padding: isMobile ? '8px 16px' : '12px 32px',
    letterSpacing: '-0.02em',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 8px 32px ${alpha(goldColor, 0.25)}`
      : `0 8px 32px ${alpha(deepGreen, 0.2)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: isDark
        ? `0 12px 40px ${alpha(goldColor, 0.35)}`
        : `0 12px 40px ${alpha(deepGreen, 0.3)}`,
      background: isDark
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, #E5D9A8 100%)`
        : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    }
  };

  const getReportTypeLabel = (reportType: string) => {
    const types: Record<string, string> = {
      'spam': 'Spam',
      'inappropriate': 'Inappropriate',
      'harassment': 'Harassment',
      'hate_speech': 'Hate Speech',
      'misinformation': 'Misinformation',
      'violence': 'Violence',
      'copyright': 'Copyright',
      'other': 'Other',
      'custom': 'Custom Reason'
    };
    return types[reportType] || reportType;
  };

  const statusButtonStyle = {
    color: getStatusColor(),
    borderColor: alpha(getStatusColor(), 0.3),
    borderRadius: '50px',
    fontWeight: 700,
    px: isMobile ? 2 : 3,
    py: isMobile ? 0.75 : 1,
    fontSize: isMobile ? '0.8rem' : '0.95rem',
    minWidth: isMobile ? 'auto' : '140px',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: getStatusColor(),
      bgcolor: alpha(getStatusColor(), isDark ? 0.1 : 0.05),
    },
    '&:disabled': {
      color: alpha(getStatusColor(), 0.5),
      borderColor: alpha(getStatusColor(), 0.2),
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'reviewed': return <FilterList fontSize={isMobile ? "small" : "medium"} />;
      case 'resolved': return <CheckCircle fontSize={isMobile ? "small" : "medium"} />;
      case 'dismissed': return <Close fontSize={isMobile ? "small" : "medium"} />;
      case 'pending': return <Warning fontSize={isMobile ? "small" : "medium"} />;
      default: return <Report fontSize={isMobile ? "small" : "medium"} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
      maxWidth={false}
      sx={{
        '& .MuiDialog-container': {
          alignItems: isMobile ? 'flex-end' : 'center',
        }
      }}
    >
      <Box sx={{
        p: isMobile ? 2 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '600px',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        maxWidth: isMobile ? '350px' : '700px',
        minWidth: isMobile ? '350px' : '600px',
      }}>
        {/* Logo and Close Button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 2 : 4,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          py: isMobile ? 1 : 0,
        }}>
          <Box
            component="img"
            src={logo}
            alt="WFHCafe Logo"
            sx={{
              height: isMobile ? 30 : 45,
              width: 'auto',
              filter: isDark ? 'brightness(1.1)' : 'none',
              maxWidth: isMobile ? '60%' : 'auto',
            }}
          />
          
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              padding: isMobile ? '4px' : '8px',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>

        {/* Title */}
        <Box sx={{ mb: isMobile ? 2 : 4 }}>
          <Typography 
            variant={isMobile ? "h6" : "h4"} 
            sx={{ 
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: isDark ? '#fff' : deepGreen,
              fontSize: isMobile ? '1.25rem' : '2rem',
              lineHeight: 1.2,
            }}
          >
            Report Details
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              fontSize: isMobile ? '0.8rem' : '1rem',
              mt: 0.5,
            }}
          >
            Review and take action on reported content
          </Typography>
        </Box>

        {/* Report Type and Status - Keep in one line even on mobile */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
        }}>
          <Chip
            icon={<Report sx={{ fontSize: isMobile ? 14 : 16 }} />}
            label={getReportTypeLabel(report.reportType)}
            sx={{
              bgcolor: alpha(goldColor, isDark ? 0.15 : 0.1),
              color: goldColor,
              fontWeight: 700,
              fontSize: isMobile ? '0.8rem' : '0.95rem',
              border: `2px solid ${alpha(goldColor, 0.3)}`,
              height: isMobile ? 32 : 36,
              '& .MuiChip-icon': { color: goldColor },
            }}
          />
          <Chip
            label={report.status}
            sx={{
              bgcolor: alpha(getStatusColor(), isDark ? 0.15 : 0.1),
              color: getStatusColor(),
              fontWeight: 700,
              fontSize: isMobile ? '0.8rem' : '0.95rem',
              textTransform: 'capitalize',
              border: `2px solid ${alpha(getStatusColor(), 0.3)}`,
              height: isMobile ? 32 : 36,
            }}
          />
        </Box>

        {/* Reported Post Info */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              mb: 2,
              fontSize: isMobile ? '1rem' : '1.1rem',
            }}
          >
            Reported Post
          </Typography>
          
          <Box sx={{ 
            p: isMobile ? 2 : 3,
            borderRadius: '16px',
            bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(5,24,20,0.02)',
            border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: isDark ? '#fff' : deepGreen,
                mb: 2,
                fontSize: isMobile ? '1rem' : '1.1rem',
                wordBreak: 'break-word',
              }}
            >
              {report.postTitle}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {loadingProfiles.author ? (
                <CircularProgress size={isMobile ? 32 : 40} sx={{ color: goldColor }} />
              ) : (
                <Avatar
                  src={postAuthorProfile?.profilePicture}
                  sx={{
                    bgcolor: alpha(goldColor, 0.1),
                    color: goldColor,
                    fontWeight: 700,
                    width: isMobile ? 40 : 48,
                    height: isMobile ? 40 : 48,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                  }}
                >
                  {getInitials(postAuthorProfile?.username || report.postAuthorUsername)}
                </Avatar>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 600,
                    color: isDark ? '#fff' : deepGreen,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                  }}
                >
                  {postAuthorProfile?.username || report.postAuthorUsername}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                  }}
                >
                  Post Author {postAuthorProfile?.id ? `(${postAuthorProfile.id.substring(0, 8)}...)` : ''}
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              onClick={handleViewPost}
              startIcon={<Visibility />}
              sx={{
                color: goldColor,
                borderColor: goldColor,
                borderRadius: '50px',
                fontWeight: 600,
                width: '100%',
                py: 1,
                '&:hover': {
                  borderColor: goldColor,
                  bgcolor: alpha(goldColor, 0.05),
                }
              }}
            >
              View Post
            </Button>
          </Box>
        </Box>

        {/* Reporter Info */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              mb: 2,
              fontSize: isMobile ? '1rem' : '1.1rem',
            }}
          >
            Reporter
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: isMobile ? 2 : 3,
            borderRadius: '16px',
            bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(5,24,20,0.02)',
            border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
          }}>
            {loadingProfiles.reporter ? (
              <CircularProgress size={isMobile ? 32 : 40} sx={{ color: goldColor }} />
            ) : (
              <Avatar
                src={reporterProfile?.profilePicture}
                sx={{
                  bgcolor: alpha(goldColor, 0.1),
                  color: goldColor,
                  fontWeight: 700,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}
              >
                {getInitials(reporterProfile?.username || report.reporterUsername)}
              </Avatar>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600,
                  color: isDark ? '#fff' : deepGreen,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}
              >
                {reporterProfile?.username || report.reporterUsername}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <Email sx={{ fontSize: isMobile ? 12 : 14 }} />
                {report.reporterEmail}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                }}
              >
                Reported on: {formatDate(report.timestamp)}
                {reporterProfile?.id ? ` • ID: ${reporterProfile.id.substring(0, 8)}...` : ''}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Report Details */}
        {(report.description || report.customReason) && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 700,
                color: isDark ? '#fff' : deepGreen,
                mb: 2,
                fontSize: isMobile ? '1rem' : '1.1rem',
              }}
            >
              Report Details
            </Typography>
            <Box sx={{
              p: isMobile ? 2 : 3,
              borderRadius: '16px',
              bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(5,24,20,0.02)',
              border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
              minHeight: isMobile ? 80 : 100,
            }}>
              <Typography sx={{ 
                color: isDark ? 'rgba(255,255,255,0.9)' : deepGreen,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                fontSize: isMobile ? '0.9rem' : '1rem',
                wordBreak: 'break-word',
              }}>
                {report.customReason || report.description || 'No additional details provided.'}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ 
          mb: 3, 
          borderColor: alpha(isDark ? '#fff' : deepGreen, 0.1) 
        }} />

        {/* Status Update Actions */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              mb: 2,
              fontSize: isMobile ? '1rem' : '1.1rem',
            }}
          >
            Update Report Status
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2, 
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start',
          }}>
            {report.status !== 'pending' && (
              <Button
                variant="outlined"
                onClick={() => handleUpdateStatus('pending')}
                disabled={actionLoading !== null}
                startIcon={actionLoading === 'status-pending' ? 
                  <CircularProgress size={isMobile ? 16 : 20} /> : 
                  <Warning fontSize={isMobile ? "small" : "medium"} />
                }
                sx={statusButtonStyle}
              >
                {isMobile ? 'Pending' : 'Mark as Pending'}
              </Button>
            )}
            {report.status !== 'reviewed' && (
              <Button
                variant="outlined"
                onClick={() => handleUpdateStatus('reviewed')}
                disabled={actionLoading !== null}
                startIcon={actionLoading === 'status-reviewed' ? 
                  <CircularProgress size={isMobile ? 16 : 20} /> : 
                  <FilterList fontSize={isMobile ? "small" : "medium"} />
                }
                sx={statusButtonStyle}
              >
                {isMobile ? 'Reviewed' : 'Mark as Reviewed'}
              </Button>
            )}
            {report.status !== 'resolved' && (
              <Button
                variant="outlined"
                onClick={() => handleUpdateStatus('resolved')}
                disabled={actionLoading !== null}
                startIcon={actionLoading === 'status-resolved' ? 
                  <CircularProgress size={isMobile ? 16 : 20} /> : 
                  <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                }
                sx={statusButtonStyle}
              >
                {isMobile ? 'Resolved' : 'Mark as Resolved'}
              </Button>
            )}
            {report.status !== 'dismissed' && (
              <Button
                variant="outlined"
                onClick={() => handleUpdateStatus('dismissed')}
                disabled={actionLoading !== null}
                startIcon={actionLoading === 'status-dismissed' ? 
                  <CircularProgress size={isMobile ? 16 : 20} /> : 
                  <Close fontSize={isMobile ? "small" : "medium"} />
                }
                sx={statusButtonStyle}
              >
                {isMobile ? 'Dismissed' : 'Mark as Dismissed'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Warning Alert for Serious Reports - Updated to use theme colors */}
        {(report.reportType === 'harassment' || report.reportType === 'hate_speech' || report.reportType === 'violence') && (
          <Alert 
            severity="warning"
            icon={<Warning />}
            sx={{ 
              mb: 3,
              borderRadius: '12px',
              bgcolor: alpha(goldColor, 0.1),
              border: `1px solid ${alpha(goldColor, 0.2)}`,
              '& .MuiAlert-icon': { color: goldColor },
              '& .MuiAlert-message': { 
                color: isDark ? 'rgba(255,255,255,0.9)' : deepGreen 
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Serious report type detected. Consider taking appropriate action.
            </Typography>
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 'auto',
          pt: 1,
          flexWrap: isMobile ? 'wrap-reverse' : 'nowrap',
          gap: isMobile ? 2 : 0,
          flexDirection: isMobile ? 'column-reverse' : 'row',
        }}>
          <Button
            onClick={onClose}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontWeight: 700,
              borderRadius: '50px',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? '0.85rem' : '1rem',
              textTransform: 'none',
              width: isMobile ? '100%' : 'auto',
              order: isMobile ? 2 : 1,
              '&:hover': {
                bgcolor: alpha(goldColor, 0.05)
              }
            }}
          >
            Close
          </Button>
          
          <Button
            variant="contained"
            onClick={handleDeletePost}
            disabled={actionLoading !== null}
            startIcon={actionLoading === 'delete-post' ? 
              <CircularProgress size={isMobile ? 16 : 20} color="inherit" /> : 
              <Delete fontSize={isMobile ? "small" : "medium"} />
            }
            sx={{
              ...gradientButtonStyle,
              width: isMobile ? '100%' : 'auto',
              order: isMobile ? 1 : 2,
              '&:disabled': {
                background: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                color: isDark ? 'rgba(5, 24, 20, 0.5)' : 'rgba(196, 182, 111, 0.5)',
                transform: 'none',
                boxShadow: 'none',
              }
            }}
          >
            Delete Post
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AdminReportsActionModal;